import type { PlayerPotInfo } from './playerPotInfo'
import type { SlotPotRecord } from './types'

export const DEFAULT_SINGLE_POT_RECORD_KEY = 'Main'

function createEmptyPlayerPotInfo(): PlayerPotInfo {
  return { potRecordMap: {} }
}

function getSlotPotRecordFromInfo(
  info: PlayerPotInfo,
  recordKey: string,
  fallback: SlotPotRecord = { level: 0, collectNum: 0 },
): SlotPotRecord {
  const r = info.potRecordMap[recordKey]
  if (r == null) {
    return { ...fallback }
  }
  return { level: r.level, collectNum: r.collectNum }
}

function upsertSlotPotRecordInInfo(
  info: PlayerPotInfo,
  recordKey: string,
  record: SlotPotRecord,
): PlayerPotInfo {
  return {
    potRecordMap: {
      ...info.potRecordMap,
      [recordKey]: { level: record.level, collectNum: record.collectNum },
    },
  }
}

function isSlotPotRecord(v: unknown): v is SlotPotRecord {
  if (v == null || typeof v !== 'object') {
    return false
  }
  const o = v as Record<string, unknown>
  return typeof o['level'] === 'number' && typeof o['collectNum'] === 'number'
}

function normalizePotRecordMap(raw: unknown): Record<string, SlotPotRecord> {
  if (raw == null || typeof raw !== 'object') {
    return {}
  }
  const src = raw as Record<string, unknown>
  const out: Record<string, SlotPotRecord> = {}
  for (const k of Object.keys(src)) {
    const v = src[k]
    if (isSlotPotRecord(v)) {
      out[k] = { level: v.level, collectNum: v.collectNum }
    }
  }
  return out
}

/** Accepts camelCase or PascalCase JSON from APIs / Redis dumps. */
function parsePlayerPotInfoJson(text: string | null): PlayerPotInfo {
  if (text == null || text === '') {
    return createEmptyPlayerPotInfo()
  }
  try {
    const parsed = JSON.parse(text) as unknown
    if (parsed == null || typeof parsed !== 'object') {
      return createEmptyPlayerPotInfo()
    }
    const o = parsed as Record<string, unknown>
    const mapRaw = o['potRecordMap'] ?? o['PotRecordMap']
    return { potRecordMap: normalizePotRecordMap(mapRaw) }
  }
  catch {
    return createEmptyPlayerPotInfo()
  }
}

/**
 * Frontend pot persistence scoped by game only (one bucket per game manager type).
 */
export interface ISlotPotRepository {
  get(): Promise<PlayerPotInfo>
  set(input: PlayerPotInfo): Promise<void>
}

/** Session key: `{gameManagerType}/Pot` — no per-player segment. */
export function slotPotGameStorageKey(gameManagerType: string): string {
  return `${gameManagerType}/Pot`
}

type StorageLike = Pick<Storage, 'getItem' | 'setItem'>

/**
 * Browser: one pot blob per game in sessionStorage.
 */
export class SessionStorageSlotPotRepository implements ISlotPotRepository {
  /**
   * All multi-pot games (e.g. one key per colour) share a single storage blob per game.
   * Mutating calls are read-modify-write, so concurrent callers (e.g. three pot colours all
   * calling `setPotRecord` in the same tick) must be serialized here — otherwise each reads the
   * same stale snapshot and the last write wins, silently dropping the others' updates.
   */
  private writeQueue: Promise<unknown> = Promise.resolve()

  constructor(
    private readonly gameName: string,
    private readonly storage: StorageLike,
  ) {}

  private storageKey(): string {
    return slotPotGameStorageKey(this.gameName)
  }

  private enqueue<T>(task: () => Promise<T>): Promise<T> {
    const result = this.writeQueue.then(task, task)
    this.writeQueue = result.catch(() => undefined)
    return result
  }

  private writeRaw(input: PlayerPotInfo): void {
    this.storage.setItem(this.storageKey(), JSON.stringify(input))
  }

  async get(): Promise<PlayerPotInfo> {
    const raw = this.storage.getItem(this.storageKey())
    const parsed = parsePlayerPotInfoJson(raw)
    if (Object.keys(parsed.potRecordMap).length === 0) {
      return createEmptyPlayerPotInfo()
    }
    return parsed
  }

  async set(input: PlayerPotInfo): Promise<void> {
    return this.enqueue(async () => this.writeRaw(input))
  }

  /** One map entry inside stored {@link PlayerPotInfo} (default key matches single-pot games). */
  async getPotRecord(recordKey: string = DEFAULT_SINGLE_POT_RECORD_KEY): Promise<SlotPotRecord> {
    const info = await this.get()
    return getSlotPotRecordFromInfo(info, recordKey)
  }

  async setPotRecord(record: SlotPotRecord, recordKey: string = DEFAULT_SINGLE_POT_RECORD_KEY): Promise<void> {
    return this.enqueue(async () => {
      const existing = await this.get()
      this.writeRaw(upsertSlotPotRecordInInfo(existing, recordKey, record))
    })
  }

  async clearPotStorage(): Promise<void> {
    return this.enqueue(async () => this.writeRaw(createEmptyPlayerPotInfo()))
  }

  /** Drop one entry from {@link PlayerPotInfo.potRecordMap}; other pot keys stay (multi-pot games). */
  async removePotRecord(recordKey: string): Promise<void> {
    return this.enqueue(async () => {
      const existing = await this.get()
      const { [recordKey]: _removed, ...rest } = existing.potRecordMap
      this.writeRaw({ potRecordMap: rest })
    })
  }
}
